import React, { useState, useEffect } from "react";
import { Card, Grid, IconButton, CircularProgress } from "@mui/material";
import { Close } from "@mui/icons-material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useNavigate, useParams } from "react-router-dom";
import api from "utils/api";
import { toast } from "react-toastify";

function EditAcademic() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [previewImage, setPreviewImage] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    icon: null,
    subCategoryTitle: "",
    vacancy: "",
  });
  const [existingIcon, setExistingIcon] = useState("");

  // Fetch academic data by ID
  useEffect(() => {
    const fetchAcademicData = async () => {
      try {
        setFetching(true);
        const response = await api.get(`/academics/${id}`);

        if (response.data.success) {
          const academic = response.data.data;
          setFormData({
            name: academic.name || "",
            icon: null,
            subCategoryTitle: academic.subCategoryTitle || "",
            vacancy: academic.vacancy || "",
          });
          setExistingIcon(academic.icon || "");
          setPreviewImage(academic.icon || null);
        }
      } catch (error) {
        console.error("Error fetching academic:", error);
        toast.error(error.response?.data?.message || "Failed to fetch academic data");
        navigate("/academic");
      } finally {
        setFetching(false);
      }
    };

    if (id) {
      fetchAcademicData();
    }
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
      if (!validTypes.includes(file.type)) {
        toast.error("Please upload a valid image file (JPEG, PNG, WEBP)");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size should be less than 5MB");
        return;
      }

      setFormData((prev) => ({
        ...prev,
        icon: file,
      }));

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!formData.subCategoryTitle.trim()) {
      toast.error("Sub Category Title is required");
      return;
    }
    if (!formData.vacancy.trim()) {
      toast.error("Vacancy is required");
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append("name", formData.name.trim());
      submitData.append("subCategoryTitle", formData.subCategoryTitle.trim());
      submitData.append("vacancy", formData.vacancy.trim());

      // Only append icon if a new file is selected
      if (formData.icon) {
        submitData.append("icon", formData.icon);
      } else {
        // Keep existing icon URL
        submitData.append("icon", existingIcon);
      }

      const response = await api.put(`/academics/${id}`, submitData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        toast.success(response.data.message || "Academic updated successfully!");
        navigate("/academic");
      }
    } catch (error) {
      console.error("Error updating academic:", error);
      toast.error(error.response?.data?.message || "Failed to update academic");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/academic");
  };

  if (fetching) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox
          py={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <ArgonTypography variant="h5" fontWeight="medium">
                  Edit Academic
                </ArgonTypography>
                <IconButton onClick={handleCancel} size="small">
                  <Close />
                </IconButton>
              </ArgonBox>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="caption" fontWeight="bold" mb={1}>
                        Name <span style={{ color: "red" }}>*</span>
                      </ArgonTypography>
                      <ArgonInput
                        type="text"
                        name="name"
                        placeholder="Enter academic name"
                        value={formData.name}
                        onChange={handleChange}
                        fullWidth
                      />
                    </ArgonBox>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="caption" fontWeight="bold" mb={1}>
                        Sub Category Title <span style={{ color: "red" }}>*</span>
                      </ArgonTypography>
                      <ArgonInput
                        type="text"
                        name="subCategoryTitle"
                        placeholder="Enter sub category title"
                        value={formData.subCategoryTitle}
                        onChange={handleChange}
                        fullWidth
                      />
                    </ArgonBox>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="caption" fontWeight="bold" mb={1}>
                        Vacancy <span style={{ color: "red" }}>*</span>
                      </ArgonTypography>
                      <ArgonInput
                        type="text"
                        name="vacancy"
                        placeholder="Enter vacancy"
                        value={formData.vacancy}
                        onChange={handleChange}
                        fullWidth
                      />
                    </ArgonBox>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="caption" fontWeight="bold" mb={1}>
                        Icon
                      </ArgonTypography>
                      <ArgonInput
                        type="file"
                        name="icon"
                        accept="image/*"
                        onChange={handleFileChange}
                        fullWidth
                        inputProps={{
                          accept: "image/jpeg,image/jpg,image/png,image/webp",
                        }}
                      />
                      <ArgonTypography variant="caption" color="text" fontSize="0.7rem" mt={0.5}>
                        Leave empty to keep existing icon
                      </ArgonTypography>
                      {previewImage && (
                        <ArgonBox mt={2}>
                          <img
                            src={previewImage}
                            alt="Preview"
                            style={{
                              width: "100px",
                              height: "100px",
                              objectFit: "cover",
                              borderRadius: "8px",
                              border: "1px solid #ddd",
                            }}
                          />
                        </ArgonBox>
                      )}
                    </ArgonBox>
                  </Grid>

                  <Grid item xs={12}>
                    <ArgonBox display="flex" justifyContent="flex-end" gap={2}>
                      <ArgonButton
                        variant="outlined"
                        color="secondary"
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        Cancel
                      </ArgonButton>
                      <ArgonButton
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loading}
                      >
                        {loading ? "Updating..." : "Update Academic"}
                      </ArgonButton>
                    </ArgonBox>
                  </Grid>
                </Grid>
              </form>
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EditAcademic;
